import { useLazyGetAnnouncementsQuery } from '@crew/apis/announcement/announcementApis'
import { GetAnnouncementsRequest } from '@crew/apis/announcement/models/getAnnouncements/request'
import { MESSAGE_AREA_MIN_HEIGHT } from '@crew/configs/constants'
import { useFetchLimit } from '@crew/hooks'
import { AnnouncementItem, useAnnouncementService } from '@crew/states'
import { TimelineDirection } from 'enums/app'
import { useCallback, useMemo } from 'react'
import { useAppSelector } from 'states/hooks'
import { getWindowDimensions } from 'utils'

// 表示用のannouncementの型
export type DisplayAnnouncementItem = {
  announcement: AnnouncementItem // announcementのID
  handleAdditionalLoading: (() => void) | undefined // 追加読込処理
}

// announcementの一覧ロード用の型
// APIリクエストの型と別定義な理由は、APIリクエストに含まれているlimitは動的にウィンドウサイズで変更するため、ここには含めなくてよい
export type LoadAnnouncementsParams = Omit<GetAnnouncementsRequest, 'limit'>

/**
 * 引数のannouncementsから表示用のアイテムリストを生成する関数
 * @param annonucement reduxに格納されているAnnouncementの一覧
 * @param loadAnnouncements Announcementをロードする関数
 * @returns 表示用アイテムリスト
 */
export const convertAnnouncementsToDisplayItems = (
  announcements: AnnouncementItem[],
  unreadOnly: boolean,
  loadAnnouncements: (params: LoadAnnouncementsParams) => void
): DisplayAnnouncementItem[] => {
  const items: DisplayAnnouncementItem[] = []

  announcements.forEach((announcement, index) => {
    // 追加読込の方向を決定する。必要無ければundefined
    const additionalLoadingDirection =
      announcement.hasMorePrev && announcement.hasMoreNext // 時間軸上で両方向の隣のメッセージが未読込だった場合はboth
        ? TimelineDirection.BothNewerAndOlder
        : announcement.hasMorePrev // 時間軸上でprev方向の隣のメッセージが未読込だった場合はolder
        ? TimelineDirection.Older
        : announcement.hasMoreNext // 時間軸上でnext方向の隣のメッセージが未読込だった場合はnewer
        ? TimelineDirection.Newer
        : undefined // 両方向の隣のメッセージが読込済だった場合は追加読込は不要

    // 追加読込処理を行う関数を生成する。必要無ければundefined
    const handleAdditionalLoading = additionalLoadingDirection
      ? () =>
          loadAnnouncements({
            unreadOnly: unreadOnly,
            criterionAnnouncementId: announcement.id,
            direction: additionalLoadingDirection,
          })
      : undefined

    items.push({
      announcement: announcement,
      handleAdditionalLoading,
    })
  })

  return items
}

export const useAnnouncementList = () => {
  const [lazyGetAnnouncementsQuery] = useLazyGetAnnouncementsQuery()

  // 取得したAnnouncementをSliceにキャッシュするためのService
  const announcementService = useAnnouncementService()

  const unreadOnly = useAppSelector((state) => state.announcement.UnreadOnly)

  // reduxに格納されているAnnouncementのDictionaryを取得する
  const announcementsDictionary = useAppSelector((state) => {
    return state.announcement.announcements.entities
  })

  // Reduxに格納されているAnnouncementのメッセージのIDリストを取得する
  // Reduxに格納されている順番で取得する必要があるので、entitiesだけでなくidsも取得しておく必要がある
  const announcementIds = useAppSelector((state) => {
    return state.announcement.announcements.ids
  })

  // 表示対象のannouncementを返す
  const announcements = useMemo(() => {
    if (!announcementsDictionary || !announcementIds) {
      return []
    }
    // id順にannouncementを設定する
    return announcementIds.reduce((result: AnnouncementItem[], id) => {
      const announcements = announcementsDictionary[id]

      if (announcements) {
        result.push(announcements)
      }
      return result
    }, [])
  }, [announcementsDictionary, announcementIds])

  // 一度にfetchするサイズ
  const fetchLimit = useFetchLimit(MESSAGE_AREA_MIN_HEIGHT, getWindowDimensions)

  // announcementの一覧をロードし、reduxに格納する関数
  const loadAnnouncements = useCallback(
    async (params: LoadAnnouncementsParams) => {
      try {
        let direction = params.direction

        if (!direction) {
          direction = TimelineDirection.Older
        }

        const data = await lazyGetAnnouncementsQuery({
          unreadOnly: params.unreadOnly,
          criterionAnnouncementId: params.criterionAnnouncementId,
          direction: direction,
          limit: fetchLimit,
        }).unwrap()

        const isLoadedOfEndOfAnnouncements =
          fetchLimit > data.announcements.length

        // 取得したAnnouncementをSliceにキャッシュする
        announcementService.addAnnouncements({
          announcements: data.announcements,
          criterionAnnouncementId: params.criterionAnnouncementId,
          direction: direction,
          isLoadedOfEndOfSourceItems: isLoadedOfEndOfAnnouncements,
          unreadCount: data.unreadCount,
        })
      } catch (err) {
        // TODO: CREW-13720の対応で、プロジェクトから退出した際にエラートーストが出てしまう問題が発生し、トースト表示をコメントアウトする暫定対応を行った
        // ただ、これにより追加ロード時にエラーがあっても画面上表示されないという状態であるため、以下タスクで恒久対応を行う
        // 現時点ではコンソールにエラーを表示するにとどめる
        // https://break-tmc.atlassian.net/browse/CREW-13724
        // toast.error(t('message.general.failedToRetrieveData'))
        console.error(err)
      }
    },
    [announcementService, fetchLimit, lazyGetAnnouncementsQuery]
  )

  const announcementInView = useCallback(
    (announcementId: string) => {
      // スクロールを戻すための対象announcementIdを設定する
      announcementService.setScrollToAnnouncementId(announcementId)
    },
    [announcementService]
  )

  const displayItems = useMemo(
    () =>
      convertAnnouncementsToDisplayItems(
        announcements,
        unreadOnly,
        loadAnnouncements
      ),
    [announcements, loadAnnouncements, unreadOnly]
  )

  // 検索処理（初期ロード・「未読のみ」チェックボックス変更時に使用）
  const searchAnnouncements = useCallback(() => {
    // ViewModelへデータを追加
    loadAnnouncements({
      unreadOnly: unreadOnly,
      criterionAnnouncementId: undefined,
      direction: TimelineDirection.Older,
    })
  }, [loadAnnouncements, unreadOnly])

  const resetAnnouncements = useCallback(() => {
    // ViewModelを初期化
    announcementService.resetAnnouncements()
  }, [announcementService])

  return {
    resetAnnouncements,
    announcementInView,
    searchAnnouncements,
    displayItems,
    unreadOnly,
  }
}
